/**
 * Convertit un état Lexical en HTML sécurisé
 * Utilisé pour le rendu côté player (web/mobile)
 */

interface LexicalNode {
  type: string;
  text?: string;
  format?: number | string[];
  style?: string;
  tag?: string;
  children?: LexicalNode[];
  [key: string]: any;
}

interface LexicalEditorState {
  root: {
    children: LexicalNode[];
    [key: string]: any;
  };
  [key: string]: any;
}

export function lexicalToHtml(editorState: LexicalEditorState | null | undefined): string {
  if (!editorState || !editorState.root) {
    return '';
  }

  return nodesToHtml(editorState.root.children);
}

/**
 * Convertit récursivement les noeuds Lexical en HTML
 */
function nodesToHtml(nodes: LexicalNode[]): string {
  if (!nodes || nodes.length === 0) {
    return '';
  }

  return nodes.map(node => nodeToHtml(node)).join('');
}

/**
 * Convertit un seul noeud Lexical en HTML
 */
function nodeToHtml(node: LexicalNode): string {
  switch (node.type) {
    case 'paragraph':
      return `<p>${nodesToHtml(node.children || [])}</p>`;

    case 'text': {
      let html = escapeHtml(node.text || '').replace(/\n/g, '<br>');

      // Appliquer les formats
      if (node.format) {
        const formats = Array.isArray(node.format) ? node.format : parseFormatBitmask(node.format);

        if (formats.includes('bold')) {
          html = `<strong>${html}</strong>`;
        }
        if (formats.includes('italic')) {
          html = `<em>${html}</em>`;
        }
        if (formats.includes('underline')) {
          html = `<u>${html}</u>`;
        }
        if (formats.includes('strikethrough')) {
          html = `<s>${html}</s>`;
        }
      }

      // Appliquer le style inline si présent
      if (node.style) {
        html = `<span style="${escapeHtml(node.style)}">${html}</span>`;
      }

      return html;
    }

    case 'linebreak':
      return '<br>';

    case 'heading': {
      const tag = node.tag || 'h1';
      return `<${tag}>${nodesToHtml(node.children || [])}</${tag}>`;
    }

    case 'list': {
      const tag = node.tag === 'ol' ? 'ol' : 'ul';
      return `<${tag}>${nodesToHtml(node.children || [])}</${tag}>`;
    }

    case 'listitem':
      return `<li>${nodesToHtml(node.children || [])}</li>`;

    default:
      // Pour les types inconnus, essayer de rendre les enfants
      if (node.children) {
        return nodesToHtml(node.children);
      }
      return '';
  }
}

/**
 * Parse le bitmask de format Lexical en array de formats
 * Lexical stocke les formats comme un nombre (bitmask):
 * 1 = bold, 2 = italic, 4 = strikethrough, 8 = underline, etc.
 */
function parseFormatBitmask(format: number): ('bold' | 'italic' | 'underline' | 'strikethrough')[] {
  const formats: ('bold' | 'italic' | 'underline' | 'strikethrough')[] = [];

  if (format & 1) formats.push('bold');
  if (format & 2) formats.push('italic');
  if (format & 4) formats.push('strikethrough');
  if (format & 8) formats.push('underline');

  return formats;
}

/**
 * Échappe les caractères HTML pour éviter les injections
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Extrait le texte brut d'un état Lexical (sans formatage)
 * Utile pour les previews, recherche, etc.
 */
export function lexicalToPlainText(editorState: LexicalEditorState | null | undefined): string {
  if (!editorState || !editorState.root) {
    return '';
  }

  return extractTextFromNodes(editorState.root.children);
}

/**
 * Extrait récursivement le texte brut des noeuds
 */
function extractTextFromNodes(nodes: LexicalNode[]): string {
  if (!nodes || nodes.length === 0) {
    return '';
  }

  return nodes.map(node => {
    if (node.type === 'text') {
      return node.text || '';
    }
    if (node.type === 'linebreak') {
      return '\n';
    }
    if (node.children) {
      return extractTextFromNodes(node.children);
    }
    return '';
  }).join('');
}
